// src/pages/sow-template/TemplatesPanel.jsx
//
// Browse/search templates -- separated from the "upload a DOCX" action
// (now a small modal instead of five inline fields crowding this list)
// and from the section/placeholder builder below it. Split out of
// SowTemplatePage.jsx.

import PropTypes from "prop-types";
import { FileText, Plus, Upload } from "lucide-react";
import { Button, DataTable, EmptyState, Input, Select, SurfaceCard } from "../../shared/ui";
import { CONTRACT_TYPE_OPTIONS } from "./sowTemplateHelpers";
import { SCOPE_OPTIONS } from "./sowTemplateOptions";

export default function TemplatesPanel({
  q,
  onQChange,
  contractType,
  onContractTypeChange,
  scopeFilter,
  onScopeFilterChange,
  rows,
  selectedId,
  onOpen,
  onNew,
  onOpenUpload,
  loading,
  canCreate,
  permissionsLoading = false,
}) {
  const columns = [
    {
      id: "name",
      header: "Name",
      accessorKey: "name",
      cell: (info) => (
        <button
          type="button"
          onClick={() => onOpen(info.row.original.id)}
          className={`text-left font-medium hover:underline ${
            info.row.original.id === selectedId ? "text-brand-600" : "text-ink"
          }`}
        >
          {info.getValue()}
        </button>
      ),
    },
    {
      id: "contract_type",
      header: "Contract Type",
      accessorFn: (r) => r.contract_type || "GLOBAL",
      width: "160px",
    },
    { id: "version", header: "Version", accessorFn: (r) => `v${r.version || "1.0"}`, width: "100px" },
    {
      id: "scope",
      header: "Scope",
      accessorFn: (r) => (r.scope === "org" ? "Org-wide" : r.scope === "account" ? "Account" : "Global"),
      width: "110px",
    },
    {
      id: "active",
      header: "Active",
      align: "center",
      width: "90px",
      accessorFn: (r) => (r.active === false ? "No" : "Yes"),
    },
  ];

  return (
    <SurfaceCard className="mb-4">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <h2 className="text-base font-semibold text-ink">Templates</h2>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={onOpenUpload}
            disabled={!canCreate}
            loading={permissionsLoading}
            title={
              permissionsLoading
                ? "Checking your permissions…"
                : !canCreate
                ? "You don't have permission to create SOW templates."
                : undefined
            }
          >
            <Upload size={14} /> Upload DOCX
          </Button>
          <Button
            size="sm"
            onClick={onNew}
            disabled={!canCreate}
            loading={permissionsLoading}
            title={
              permissionsLoading
                ? "Checking your permissions…"
                : !canCreate
                ? "You don't have permission to create SOW templates."
                : undefined
            }
          >
            <Plus size={14} /> New Template
          </Button>
        </div>
      </div>

      <div className="flex gap-3 flex-wrap mb-4">
        <div className="w-64">
          <Input placeholder="Search templates…" value={q} onChange={(e) => onQChange(e.target.value)} />
        </div>
        <div className="w-52">
          <Select
            options={CONTRACT_TYPE_OPTIONS}
            value={contractType}
            onChange={(e) => onContractTypeChange(e.target.value)}
          />
        </div>
        <div className="w-48">
          <Select
            options={SCOPE_OPTIONS}
            value={scopeFilter}
            onChange={(e) => onScopeFilterChange(e.target.value)}
          />
        </div>
      </div>

      {rows.length === 0 && !loading ? (
        <EmptyState icon={FileText} title="No templates found" description="Create one or upload a DOCX to get started." />
      ) : (
        <DataTable
          table={{ columns, rows }}
          entriesPerPage={{ defaultValue: 10, entries: [10, 20, 50] }}
          emptyMessage="No templates found."
        />
      )}
    </SurfaceCard>
  );
}

TemplatesPanel.propTypes = {
  q: PropTypes.string.isRequired,
  onQChange: PropTypes.func.isRequired,
  contractType: PropTypes.string.isRequired,
  onContractTypeChange: PropTypes.func.isRequired,
  scopeFilter: PropTypes.string.isRequired,
  onScopeFilterChange: PropTypes.func.isRequired,
  rows: PropTypes.array.isRequired,
  selectedId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onOpen: PropTypes.func.isRequired,
  onNew: PropTypes.func.isRequired,
  onOpenUpload: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  canCreate: PropTypes.bool.isRequired,
  permissionsLoading: PropTypes.bool,
};
